import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
  where,
  type Firestore,
  type Unsubscribe,
} from 'firebase/firestore';

export type ScheduledCommentTarget = 'taskComments' | 'board';

export type ScheduledCommentRow = {
  id: string;
  authorId: string;
  status: string;
  scheduledAt: Date;
  target: ScheduledCommentTarget;
  threadKey: string;
  bodyPreview: string;
  commentPayload?: Record<string, unknown> | null;
  dealId?: string | null;
  nodeId?: string | null;
  content?: string | null;
  contentHtml?: string | null;
  mentions?: unknown;
};

export function scheduledCommentThreadKey(args: {
  target: ScheduledCommentTarget;
  taskId?: string | null;
  groupCommentKey?: string | null;
  dealId?: string | null;
  nodeId?: string | null;
}): string {
  if (args.target === 'board') {
    return `board:${String(args.dealId || '').trim()}:${String(args.nodeId || '').trim()}`;
  }
  const groupKey = String(args.groupCommentKey || '').trim();
  if (groupKey) return `group:${groupKey}`;
  return `task:${String(args.taskId || '').trim()}`;
}

/** Presets in the user's local timezone. */
export const SCHEDULE_COMMENT_PRESETS: Array<{
  id: string;
  label: string;
  resolveAt: (from?: Date) => Date;
}> = [
  {
    id: '1h',
    label: 'In 1 hour',
    resolveAt: (from = new Date()) => {
      const d = new Date(from);
      d.setMinutes(0, 0, 0);
      d.setHours(d.getHours() + 1);
      if (d.getTime() <= from.getTime()) d.setHours(d.getHours() + 1);
      return d;
    },
  },
  {
    id: 'tomorrow8',
    label: 'Tomorrow 8 AM',
    resolveAt: (from = new Date()) => {
      const d = new Date(from);
      d.setDate(d.getDate() + 1);
      d.setHours(8, 0, 0, 0);
      return d;
    },
  },
  {
    id: 'tomorrow9',
    label: 'Tomorrow 9 AM',
    resolveAt: (from = new Date()) => {
      const d = new Date(from);
      d.setDate(d.getDate() + 1);
      d.setHours(9, 0, 0, 0);
      return d;
    },
  },
  {
    id: 'nextMon8',
    label: 'Next Monday 8 AM',
    resolveAt: (from = new Date()) => {
      const d = new Date(from);
      const day = d.getDay();
      const add = day === 0 ? 1 : day === 1 ? 7 : 8 - day;
      d.setDate(d.getDate() + add);
      d.setHours(8, 0, 0, 0);
      return d;
    },
  },
];

export function toDatetimeLocalValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export async function createScheduledTaskComment(args: {
  db: Firestore;
  authorId: string;
  authorName: string;
  authorInitials?: string;
  scheduledAt: Date;
  commentPayload: Record<string, unknown>;
  threadKey: string;
  projectId?: string | null;
  bodyPreview: string;
}): Promise<string> {
  if (!(args.scheduledAt.getTime() > Date.now())) {
    throw new Error('Scheduled time must be in the future.');
  }
  const payload = { ...args.commentPayload };
  delete payload.createdAt;
  const ref = await addDoc(collection(args.db, 'scheduledComments'), {
    authorId: args.authorId,
    authorName: args.authorName,
    authorInitials: args.authorInitials || null,
    status: 'pending',
    target: 'taskComments',
    scheduledAt: Timestamp.fromDate(args.scheduledAt),
    commentPayload: payload,
    threadKey: args.threadKey,
    projectId: args.projectId || null,
    bodyPreview: args.bodyPreview.slice(0, 200),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function createScheduledBoardComment(args: {
  db: Firestore;
  authorId: string;
  authorName: string;
  scheduledAt: Date;
  dealId: string;
  nodeId: string;
  content: string;
  contentHtml?: string | null;
  mentions?: unknown;
  projectId?: string | null;
}): Promise<string> {
  if (!(args.scheduledAt.getTime() > Date.now())) {
    throw new Error('Scheduled time must be in the future.');
  }
  const threadKey = scheduledCommentThreadKey({
    target: 'board',
    dealId: args.dealId,
    nodeId: args.nodeId,
  });
  const ref = await addDoc(collection(args.db, 'scheduledComments'), {
    authorId: args.authorId,
    authorName: args.authorName,
    status: 'pending',
    target: 'board',
    scheduledAt: Timestamp.fromDate(args.scheduledAt),
    dealId: args.dealId,
    nodeId: args.nodeId,
    content: args.content,
    contentHtml: args.contentHtml || null,
    mentions: args.mentions || null,
    threadKey,
    projectId: args.projectId || null,
    bodyPreview: args.content.slice(0, 200),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export function subscribeOwnPendingScheduledComments(args: {
  db: Firestore;
  authorId: string;
  threadKey: string;
  onChange: (rows: ScheduledCommentRow[]) => void;
}): Unsubscribe {
  const q = query(
    collection(args.db, 'scheduledComments'),
    where('authorId', '==', args.authorId),
    where('threadKey', '==', args.threadKey),
    where('status', '==', 'pending'),
  );
  return onSnapshot(q, (snap) => {
    const rows: ScheduledCommentRow[] = [];
    snap.forEach((d) => {
      const data = d.data();
      rows.push({
        id: d.id,
        authorId: String(data.authorId || ''),
        status: String(data.status || ''),
        scheduledAt: data.scheduledAt?.toDate?.() || new Date(),
        target: (data.target === 'board' ? 'board' : 'taskComments') as ScheduledCommentTarget,
        threadKey: String(data.threadKey || ''),
        bodyPreview: String(data.bodyPreview || data.content || data.commentPayload?.body || ''),
        commentPayload: (data.commentPayload as Record<string, unknown>) || null,
        dealId: data.dealId || null,
        nodeId: data.nodeId || null,
        content: data.content || null,
        contentHtml: data.contentHtml || null,
        mentions: data.mentions,
      });
    });
    rows.sort((a, b) => a.scheduledAt.getTime() - b.scheduledAt.getTime());
    args.onChange(rows);
  });
}

export async function cancelScheduledComment(db: Firestore, id: string): Promise<void> {
  await updateDoc(doc(db, 'scheduledComments', id), {
    status: 'cancelled',
    updatedAt: serverTimestamp(),
  });
}

export async function rescheduleComment(
  db: Firestore,
  id: string,
  scheduledAt: Date,
): Promise<void> {
  if (!(scheduledAt.getTime() > Date.now())) {
    throw new Error('Scheduled time must be in the future.');
  }
  await updateDoc(doc(db, 'scheduledComments', id), {
    scheduledAt: Timestamp.fromDate(scheduledAt),
    status: 'pending',
    updatedAt: serverTimestamp(),
  });
}

export async function deleteScheduledComment(db: Firestore, id: string): Promise<void> {
  await deleteDoc(doc(db, 'scheduledComments', id));
}
